import * as XLSX from 'xlsx'

export function normalizeHeader(raw: string): string {
  // multiline headers: only match on the first line
  const firstLine = raw.split(/[\r\n]/)[0] ?? ''
  return firstLine
    .toLowerCase()
    .replace(/\t+/g, ' ')
    .replace(/[-‐‑–—]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '')
    .trim()
}

export type ParsedField =
  | 'function_name'
  | 'system_description'
  | 'vendor_name_raw'
  | 'system_name'
  | 'contact_full_name'
  | 'contact_email'
  | 'contact_phone'
  | 'type_approval_status'
  | 'shipyard_contact_raw'
  | 'shipyard_contact_email_raw'
  | 'remark'

const ALIAS_ENTRIES: Array<[string[], ParsedField]> = [
  [['functions', 'function', '기능'],                                                     'function_name'],
  [['system', '시스템'],                                                                  'system_description'],
  [['vendor name', 'vendor', 'maker', 'maker name',
    '공급 업체명', '공급업체명', '업체명', '제조사', '제작사'],                             'vendor_name_raw'],
  [['system name', 'system type', 'model', '시스템명', '시스템 명', '장비명',
    'system description', '시스템 설명'],                                                  'system_name'],
  [['person in charge', 'pic', 'responsible person',
    '담당자 성함', '담당자', '성함', '담당자명'],                                           'contact_full_name'],
  [['e mail', 'email', 'mail', '메일 주소', '메일주소', '이메일'],                           'contact_email'],
  [['tel', 'telephone', 'phone', 'tel.', 'tel no',
    '연락처', '전화번호', '전화'],                                                         'contact_phone'],
  [['type approval status', 'ta status', 'type approval',
    '사이버 보안관련', '타입어프루벌'],                                                     'type_approval_status'],
  // The SHIPYARD's own person, not the vendor's. Kept distinct from
  // contact_full_name on purpose: a yard clerk's name is not a vendor
  // contact and must never be promoted into one.
  //
  // 'Yard PIC (야드 담당자)' is the exact header the SHI export writes,
  // and it has to be listed literally. Lookup is an exact Map.get on the
  // normalised string, and normalizeHeader only strips punctuation at
  // the ENDS — so this normalises to "yard pic (야드 담당자", with the
  // opening bracket intact. A bare 'yard pic' alias would never have
  // matched our own form.
  [['shi 담당자', '조선소 담당자', '담당자 정보',
    'shipyard contact', 'yard contact',
    'yard pic', 'Yard PIC (야드 담당자)', '야드 담당자'],                                  'shipyard_contact_raw'],
  [['remark', 'remarks', 'note', 'notes', '비고'],                                        'remark'],
]

export const FIELD_COLUMN_MAP = new Map<string, ParsedField>()
for (const [aliases, field] of ALIAS_ENTRIES) {
  for (const alias of aliases) {
    FIELD_COLUMN_MAP.set(normalizeHeader(alias), field)
  }
}

// minimum header matches to accept a row as the header row
const HEADER_MATCH_THRESHOLD = 3

// headers kept in raw_data only, not mapped to parsed fields
// ─── two columns both called E-mail ─────────────────────────────────────
//
// An SHI form carries the yard PIC's address at D ("E-Mail") and the
// vendor contact's at J ("E-mail"). Both normalise to the same key, so
// an exact Map.get cannot tell them apart — and the contact-header scan
// takes the FIRST match, which is the yard's. Left alone, a returned SHI
// form would have filed the shipyard clerk's address as the vendor's.
//
// DISAMBIGUATED BY POSITION, anchored to a neighbour rather than to a
// column letter: the email column IMMEDIATELY RIGHT of the Yard PIC
// column is the yard's. That survives the whole table being reordered
// again, which a fixed index would not.
//
// The header text is duplicated from the export builder on purpose —
// src/ cannot import from server/ — and the two are pinned equal by a
// test, the same arrangement SOURCE_COL_HEADER already lives under.
const YARD_PIC_HEADER_NORM = normalizeHeader('Yard PIC (야드 담당자)')

export function resolveHeaderField(
  norm: string,
  prevNorm: string | null,
): ParsedField | undefined {
  const field = FIELD_COLUMN_MAP.get(norm)
  if (field === 'contact_email' && prevNorm === YARD_PIC_HEADER_NORM) {
    return 'shipyard_contact_email_raw'
  }
  return field
}

export interface ImportContact {
  full_name?: string
  email?: string
  phone?: string
}

export interface ParsedImportRow {
  function_name?:        string
  system_description?:   string
  vendor_name_raw?:      string
  system_name?:          string
  type_approval_status?: string
  // A/B/C grade from Vendor Tracking imports. null/undefined for
  // System Register imports (no grade column in that sheet)
  cbs_grade?:            string | null
  // shipyard's internal contact (e.g. SHI 담당자)
  shipyard_contact_raw?: string
  shipyard_contact_email_raw?: string
  // freeform remark/비고
  remark?:              string
  // primary contact (single-contact rows, or first of multi)
  contact_full_name?: string
  contact_email?:     string
  contact_phone?:     string
  // multiple contacts detected in one cell
  contacts?:      ImportContact[]
  multi_contact?: boolean
}

export type WorkbookFormat = 'system_register' | 'vendor_tracking'

export type RowClassification = 'clean' | 'warning' | 'invalid'

export interface StagedRow {
  row_no:            number
  raw_data:          Record<string, unknown>
  parsed_data:       ParsedImportRow
  validation_errors: string[]
  is_valid:          boolean
  classification:    RowClassification
  skipped?:          boolean
  skip_reason?:      string
}

// diagnostic counters surfaced on every parse. logged by the dispatcher,
// readable in browser console for "why only N rows?" post-mortems. shape
// identical across formats. some categories stay 0 for one format or the
// other (e.g. Vendor Tracking has no section-header concept, System
// Register doesn't reject rows with missing vendor)
export interface ParseTelemetry {
  format:                       WorkbookFormat
  header_row:                   number   // 1-indexed for user display
  last_data_row:                number   // 1-indexed, 0 if no rows kept
  rows_kept:                    number
  rows_skipped_empty:           number   // structurally empty (no system/vendor/system_name)
  rows_skipped_section_header:  number   // SR: function_name only, no system/vendor
  rows_skipped_explanation:     number   // SR: Q-block / criteria prose / etc.
  rows_skipped_invalid:         number   // VT: row with no vendor and no system
  stop_reason:                  'end_of_sheet' | 'streak_break' | 'distance_break'
}

export interface ParseResult {
  format:        WorkbookFormat
  rows:          StagedRow[]
  skipped_count: number
  sheet_used:    string
  header_row:    number
  telemetry:     ParseTelemetry
  // below fields expose raw parsing state for the UI's column-mapping editor.
  // Vendor Tracking populates them with sensible values, but its column layout is
  // fixed by the workbook spec, so the editor is hidden for that format in the UI
  headerIdx:     number                       // 0-based index of the header row
  headers:       string[]                     // raw header cell texts
  rawMatrix:     unknown[][]                  // sheet matrix after merged-cell + function fill-down
  colMap:        Record<string, number>       // auto-detected field_name → col_index
}

// sheets present on unrecognized-format rejections only, carries
// the per-sheet description for the import_parse_rejected audit
export type ParseError = { error: string; sheets?: Array<{ name: string; headers: string[] }> }
export function isParseError(r: ParseResult | ParseError): r is ParseError {
  return 'error' in r
}

function selectSheet(
  wb: XLSX.WorkBook
): { name: string; ws: XLSX.WorkSheet } | null {
  const exact = wb.SheetNames.find(n => n === '2. System Information')
  if (exact) return { name: exact, ws: wb.Sheets[exact] }

  const fuzzy = wb.SheetNames.find(n => {
    const norm = n.toLowerCase().replace(/\s+/g, ' ').trim()
    return norm.includes('system') && norm.includes('information')
  })
  if (fuzzy) return { name: fuzzy, ws: wb.Sheets[fuzzy] }

  // fallback to second sheet, header detection will validate
  if (wb.SheetNames.length >= 2) {
    return { name: wb.SheetNames[1], ws: wb.Sheets[wb.SheetNames[1]] }
  }

  return null
}

function fillMergedCells(
  matrix: unknown[][],
  ws: XLSX.WorkSheet
): unknown[][] {
  const merges: XLSX.Range[] = (ws['!merges'] as XLSX.Range[]) ?? []
  if (merges.length === 0) return matrix

  // `matrix` comes from sheet_to_json({header:1}), RELATIVE to the
  // sheet's used-range origin. for a sheet whose !ref is "B2:S114",
  // matrix[0][0] is cell B2, not A1. but !merges ranges are ABSOLUTE
  // (A1-based). translate every merge into the matrix's frame by
  // subtracting the range origin, else a vendor merge declared at D49:D54
  // lands on the wrong column (and reads a bogus top-left),
  // leaving the real merged values blank. adaptive: any origin, any
  // column, any file
  const origin = ws['!ref'] ? XLSX.utils.decode_range(ws['!ref']).s : { r: 0, c: 0 }

  const filled = matrix.map(row => [...row])

  for (const merge of merges) {
    const sr = merge.s.r - origin.r, sc = merge.s.c - origin.c
    const er = merge.e.r - origin.r, ec = merge.e.c - origin.c
    if (sr < 0 || sc < 0) continue   // merge outside the materialized range
    const topVal = filled[sr]?.[sc]
    for (let r = sr; r <= er; r++) {
      for (let c = sc; c <= ec; c++) {
        if ((r !== sr || c !== sc) && filled[r]) {
          filled[r][c] = topVal
        }
      }
    }
  }
  return filled
}

function detectHeader(
  matrix: unknown[][]
): { headerIdx: number; colMap: Map<number, ParsedField> } | null {
  for (let i = 0; i < Math.min(15, matrix.length); i++) {
    const row = matrix[i]
    const colMap = new Map<number, ParsedField>()
    for (let c = 0; c < row.length; c++) {
      const norm = normalizeHeader(String(row[c] ?? ''))
      const field = resolveHeaderField(norm, c > 0 ? normalizeHeader(String(row[c - 1] ?? '')) : null)
      if (field) colMap.set(c, field)
    }
    if (colMap.size >= HEADER_MATCH_THRESHOLD) {
      return { headerIdx: i, colMap }
    }
  }
  return null
}

function isPlaceholder(v: string): boolean {
  return /^[-–—\s]*$/.test(v)
}

function hasMeaningfulData(
  row: unknown[],
  colMap: Map<number, ParsedField>
): boolean {
  for (const [c, field] of colMap) {
    if (!['system_description', 'system_name', 'vendor_name_raw'].includes(field)) continue
    const val = String(row[c] ?? '').trim()
    if (val && !isPlaceholder(val)) return true
  }
  return false
}

// SHI workbooks append Q1-Q4 explanation blocks below real rows.
// prose in system_description, no vendor/contact, must be skipped
function isExplanationRow(p: ParsedImportRow): {
  isExplanation: boolean
  reason?: string
} {
  const fn      = (p.function_name        ?? '').trim()
  const sys     = (p.system_description   ?? '').trim()
  const vendor  = (p.vendor_name_raw      ?? '').trim()
  const sysName = (p.system_name          ?? '').trim()
  const cName   = (p.contact_full_name    ?? '').trim()
  const email   = (p.contact_email        ?? '').trim()
  const phone   = (p.contact_phone        ?? '').trim()

  // section headers in function_name cell
  if (/^Q[1-4]\b/i.test(fn))    return { isExplanation: true, reason: 'Q-section header (Q1/Q2/Q3/Q4)' }
  if (fn.includes('설명'))      return { isExplanation: true, reason: 'Explanation block (설명)' }
  if (fn.includes('참조'))      return { isExplanation: true, reason: 'Reference note (참조)' }
  if (fn.startsWith('*'))       return { isExplanation: true, reason: 'Footnote row (*)' }

  // merged explanation cell (function_name > 80 chars). ONLY when the row
  // carries no real system evidence — LR registers have legitimate long
  // function labels ("Safety systems whose disruption or functional
  // impairing may pose risks to ship operations", 89 chars) merged across
  // whole system groups; killing those rows dropped the entire group
  if (fn.length > 80 && !vendor && !sysName && !cName && !email && !phone) {
    return { isExplanation: true, reason: 'Merged explanation cell in function column' }
  }

  // criteria prose in system_description cell
  const criteriaPatterns: RegExp[] = [
    /^the system shall/i,
    /^the system should/i,
    /^it is necessary/i,
    /^there should be/i,
    /^if a port is/i,
    /^a system that is/i,
    /^external ports must/i,
    /^generally,?\s*systems/i,
    /shall be isolated/i,
    /shall not have/i,
    /shall be located/i,
    /shall not provide/i,
    /do not meet/i,
  ]
  if (criteriaPatterns.some(r => r.test(sys))) {
    return { isExplanation: true, reason: 'Criteria text, not a system' }
  }

  // all key fields empty + long description block
  const allEmpty = !vendor && !sysName && !cName && !email && !phone
  if (allEmpty && sys.length > 100) {
    return { isExplanation: true, reason: 'Description block (no vendor/contact/system_name)' }
  }

  return { isExplanation: false }
}

function splitContacts(
  rawName:  string | undefined,
  rawEmail: string | undefined,
  rawPhone: string | undefined
): { contacts: ImportContact[]; isMulti: boolean; ambiguous: boolean } {
  const names  = splitLines(rawName ?? '')
  const emails = (rawEmail ?? '')
    .split(/[\n\r;,]+/)
    .map(s => s.trim().toLowerCase())
    .filter(Boolean)
  const phones = splitLines(rawPhone ?? '')

  const isMulti = names.length > 1 || emails.length > 1

  if (!isMulti) {
    const c: ImportContact = {}
    if (names[0])  c.full_name = names[0]
    if (emails[0]) c.email = emails[0]
    if (phones[0]) c.phone = phones[0]
    return { contacts: Object.keys(c).length ? [c] : [], isMulti: false, ambiguous: false }
  }

  // align multiple contacts by line position
  const count     = Math.max(names.length, emails.length)
  const ambiguous = names.length > 0 && emails.length > 0 && names.length !== emails.length

  const contacts: ImportContact[] = []
  for (let i = 0; i < count; i++) {
    const c: ImportContact = {}
    c.full_name = names[i]  ?? names[0]
    c.email     = emails[i] ?? emails[0]
    c.phone     = phones[i] ?? phones[0]
    if (!c.full_name) delete c.full_name
    if (!c.email)     delete c.email
    if (!c.phone)     delete c.phone
    if (Object.keys(c).length) contacts.push(c)
  }

  return { contacts, isMulti: true, ambiguous }
}

function splitLines(s: string): string[] {
  return s.split(/[\n\r]+/).map(x => x.trim()).filter(Boolean)
}

// sheet-name sniff for ASSESSMENT workbooks (the per-system vendor
// reply / legacy RA formats handled by POST /parse-assessment, NOT the
// project-level import formats this module parses). mirrors the
// name-level fast-paths of the backend's workbookFormatDetector.
//
// HINT ONLY, appended to the unrecognized-format error so a vendor
// reply dropped at the project level explains itself. deliberately
// NOT used to divert routing: the 2026-06-12 incident proved sheet
// names like "02_Vendor_Response_Form" also appear on system-register
// source workbooks, so names can't authoritatively classify intent.
export function looksLikeAssessmentWorkbook(wb: XLSX.WorkBook): boolean {
  const names = wb.SheetNames
  const hasCbsOr01     = names.some(n => n.includes('CBS') || n.startsWith('01'))
  const hasVendorResp  = names.some(n => n.includes('Vendor_Response') || n.startsWith('02_Vendor') || /vendor.*response/i.test(n))
  const hasLegacyGen   = names.some(n => /^1\.\s*General/i.test(n))
  const hasLegacyRa    = names.some(n => /^2\.\s*Risk/i.test(n))
  return hasCbsOr01 || hasVendorResp || (hasLegacyGen && hasLegacyRa)
}

// per-sheet description for the self-describing rejection: sheet name
// + the first non-empty row's first ≤10 non-empty cells. mirror of the
// backend detector's describeSheets (server/src/lib/
// workbookFormatDetector.ts) so FE and server rejections read the same
export interface SheetDescription { name: string; headers: string[] }

export function describeWorkbookSheets(wb: XLSX.WorkBook): SheetDescription[] {
  return wb.SheetNames.map(name => {
    const ws = wb.Sheets[name]
    const matrix = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: '', raw: false }) as unknown[][]
    for (let r = 0; r < Math.min(10, matrix.length); r++) {
      const headers = (matrix[r] ?? [])
        .map(c => String(c ?? '').trim())
        .filter(Boolean)
        .slice(0, 10)
        .map(h => (h.length > 30 ? h.slice(0, 30) + '…' : h))
      if (headers.length > 0) return { name, headers }
    }
    return { name, headers: [] }
  })
}

// top-level dispatcher. sniff workbook format, route to the right parser.
// returns ParseError if no format matches, caller surfaces the message.
// logs a one-line telemetry summary so "why only N rows?" questions can be
// answered from the browser console without re-running the parse
export function parseWorkbook(wb: XLSX.WorkBook): ParseResult | ParseError {
  const fmt = detectFormat(wb)
  let result: ParseResult | ParseError
  if (fmt === 'vendor_tracking') result = parseVendorTracking(wb)
  else if (fmt === 'system_register') result = parseSystemRegister(wb)
  else {
    // self-describing rejection (same instrument pattern as the
    // backend's 4464fdf treatment of parse-assessment): list each
    // sheet with its first-row headers so the next unknown shape
    // explains itself in the error instead of needing log
    // archaeology. the structured sheets array also rides on the
    // ParseError so ImportWorkbookPage can ship it to the
    // import_parse_rejected audit endpoint.
    const sheets = describeWorkbookSheets(wb)
    const found = sheets
      .map(s => `"${s.name}" [headers: ${s.headers.length > 0 ? s.headers.join(' | ') : '(empty)'}]`)
      .join(', ')
    const assessmentHint = looksLikeAssessmentWorkbook(wb)
      ? ' If this is a per-system vendor reply, open that system\'s page and drop it there instead.'
      : ''
    return {
      error:
        `Workbook format not recognized. Found sheets: ${found} — expected either a ` +
        '"Vendor Tracking" sheet (with "Vendor Data Tracking" title) or a ' +
        '"2. System Information" sheet with standard column headers.' +
        assessmentHint,
      sheets,
    }
  }
  if (!isParseError(result)) {

    console.info('[workbookParser] parsed', result.telemetry)
  }
  return result
}

function detectFormat(wb: XLSX.WorkBook): WorkbookFormat | null {
  // Vendor Tracking: sheet name + A1 cell content sentinel
  const vtSheet = wb.SheetNames.find(n => /^vendor[\s_]*tracking$/i.test(n.trim()))
  if (vtSheet) {
    const ws = wb.Sheets[vtSheet]
    const a1 = ws['A1']?.v
    if (typeof a1 === 'string' && /^vendor\s+data\s+tracking/i.test(a1.trim())) {
      return 'vendor_tracking'
    }
  }

  // System Register: existing sheet selection + header detection.
  // probing here means we don't false-positive other 2-sheet workbooks
  // through selectSheet's fallback branch
  const selected = selectSheet(wb)
  if (selected) {
    const matrix = XLSX.utils.sheet_to_json<unknown[]>(selected.ws, {
      header: 1, defval: '', raw: false,
    }) as unknown[][]
    if (matrix.length > 0) {
      const filled = fillMergedCells(matrix, selected.ws)
      if (detectHeader(filled)) return 'system_register'
    }
  }

  return null
}

function parseSystemRegister(wb: XLSX.WorkBook): ParseResult | ParseError {
  const selected = selectSheet(wb)
  if (!selected) {
    return {
      error:
        'Sheet "2. System Information" not found. ' +
        'The workbook must have at least 2 sheets.',
    }
  }

  const ws = selected.ws

  const rawMatrix = XLSX.utils.sheet_to_json<unknown[]>(ws, {
    header: 1,
    defval: '',
    raw:    false,
  }) as unknown[][]

  if (rawMatrix.length === 0) {
    return { error: `Sheet "${selected.name}" is empty.` }
  }

  // apply Excel merge ranges: propagates merged values (category headers, multi-row vendors/contacts) down
  const matrix = fillMergedCells(rawMatrix, ws)

  const headerInfo = detectHeader(matrix)
  if (!headerInfo) {
    return {
      error:
        'Could not find the header row in the first 10 rows. ' +
        'Expected columns: Functions, System, Vendor Name, System Name, ' +
        'Person In Charge, E-Mail, TEL, Type Approval Status.',
    }
  }

  const { headerIdx, colMap } = headerInfo

  // inverted colMap for field-name → col-index lookups
  const colByField: Record<string, number> = {}
  for (const [c, field] of colMap) colByField[field] = c

  // restore raw values for system_description and system_name columns. Excel merges here
  // would wrongly duplicate "Emergency shut-down" (or similar) across multiple rows.
  // each system row must keep its own value (or blank, later skipped as empty)
  const restoreCols = [colByField.system_description, colByField.system_name]
    .filter((c): c is number => c !== undefined)
  for (const col of restoreCols) {
    for (let r = headerIdx + 1; r < matrix.length; r++) {
      if (matrix[r]) matrix[r][col] = rawMatrix[r]?.[col] ?? ''
    }
  }

  // blank-cell fill-down: ONLY for function_name (Korean shipyards often leave category blank on
  // continuation rows). must NEVER include system_description, system_name, or vendor columns
  const fillDownCols: number[] = [colByField.function_name]
    .filter((c): c is number => c !== undefined)
  for (const col of fillDownCols) {
    let lastValue: unknown = ''
    for (let r = headerIdx + 1; r < matrix.length; r++) {
      const cell = matrix[r]?.[col]
      const s = String(cell ?? '').trim()
      if (!s || /^[-–—\s]*$/.test(s)) {
        if (lastValue && matrix[r]) matrix[r][col] = lastValue
      } else {
        lastValue = cell
      }
    }
  }

  const built = buildStagedRows(matrix, headerIdx, colMap)
  const { rows, skipped_count } = built

  const headerCells = matrix[headerIdx] ?? []
  const headers: string[] = []
  for (let c = 0; c < headerCells.length; c++) {
    headers.push(String(headerCells[c] ?? '').trim())
  }

  const colMapRecord: Record<string, number> = {}
  for (const [c, field] of colMap) colMapRecord[field] = c

  const telemetry: ParseTelemetry = {
    format: 'system_register',
    header_row: headerIdx + 1,
    last_data_row: built.last_data_row,
    rows_kept: rows.filter(r => !r.skipped).length,
    rows_skipped_empty: built.rows_skipped_empty,
    rows_skipped_section_header: built.rows_skipped_section_header,
    rows_skipped_explanation: built.rows_skipped_explanation,
    rows_skipped_invalid: 0,   // SR doesn't reject for missing vendor/system, warnings only
    stop_reason: built.stop_reason,
  }

  return {
    format: 'system_register',
    rows,
    skipped_count,
    sheet_used: selected.name,
    header_row: headerIdx + 1,
    telemetry,
    headerIdx,
    headers,
    rawMatrix: matrix,
    colMap: colMapRecord,
  }
}

// structurally-empty rows (no system/vendor/system_name and no
// function_name to mark a section divider) signal end of sheet data.
// section dividers and explanation rows are mid-sheet noise, don't
// count toward the break, skipped silently. threshold matches
// parseVendorTracking's VT_EMPTY_STREAK_STOP for consistency
const STRUCTURALLY_EMPTY_BREAK = 10

// hard upper bound on the gap between the last kept row and the cursor.
// catches pathological sheets (e.g. 60 rows of trailing prose with no
// strict-empty rows in between) where the streak break alone wouldn't
// trip. set generously, real sheets have at most ~10 trailing notes
const MAX_DISTANCE_FROM_LAST_KEPT = 50

function buildStagedRows(
  matrix: unknown[][],
  headerIdx: number,
  colMap: Map<number, ParsedField>,
): {
  rows: StagedRow[]
  skipped_count: number
  last_data_row: number   // 1-indexed Excel row of last kept row (0 if none)
  rows_skipped_empty: number
  rows_skipped_section_header: number
  rows_skipped_explanation: number
  stop_reason: 'end_of_sheet' | 'streak_break' | 'distance_break'
} {
  const headerRow = matrix[headerIdx] ?? []
  const colLabels = new Map<number, string>()
  for (let c = 0; c < headerRow.length; c++) {
    const label = String(headerRow[c] ?? '').trim()
    colLabels.set(c, label || `col_${c}`)
  }

  const rows: StagedRow[] = []
  let consecutiveStructurallyEmpty = 0
  let lastKeptRowIdx = headerIdx
  let rows_skipped_empty = 0
  let rows_skipped_section_header = 0
  let rows_skipped_explanation = 0
  let stop_reason: 'end_of_sheet' | 'streak_break' | 'distance_break' = 'end_of_sheet'

  for (let r = headerIdx + 1; r < matrix.length; r++) {
    // distance safety net: if we've gone 50+ rows past the last real
    // data row without keeping anything, stop walking. catches sheets
    // that end with a long prose tail
    if (r - lastKeptRowIdx > MAX_DISTANCE_FROM_LAST_KEPT) {
      stop_reason = 'distance_break'
      break
    }

    const rowData = matrix[r]
    const rowNo   = r + 1  // 1-based Excel row number

    const raw_data: Record<string, unknown> = {}
    for (let c = 0; c < rowData.length; c++) {
      raw_data[colLabels.get(c) ?? `col_${c}`] = rowData[c]
    }

    // skip branch 1: structurally empty (no system/vendor/system_name and
    // not even a function_name divider). counts toward the break streak
    if (!hasMeaningfulData(rowData, colMap)) {
      rows_skipped_empty++
      consecutiveStructurallyEmpty++
      rows.push({
        row_no:            rowNo,
        raw_data,
        parsed_data:       {},
        validation_errors: [],
        is_valid:          false,
        classification:    'invalid',
        skipped:           true,
        skip_reason:       'Empty or placeholder row',
      })
      if (consecutiveStructurallyEmpty >= STRUCTURALLY_EMPTY_BREAK) {
        stop_reason = 'streak_break'
        break
      }
      continue
    }

    const parsed_data: ParsedImportRow = {}
    for (const [c, field] of colMap) {
      const val = String(rowData[c] ?? '').trim()
      if (val && !isPlaceholder(val) && !(parsed_data as Record<string, unknown>)[field]) {
        ;(parsed_data as Record<string, unknown>)[field] = val
      }
    }

    // skip branch 2: section header row, function_name only, system/vendor
    // both empty (e.g. "Anchoring and mooring / System=-"). mid-sheet
    // navigation marker, does NOT count toward the structurally-empty
    // streak so dense divider clusters don't kill the parse
    const psd = (parsed_data.system_description ?? '').trim()
    const psn = (parsed_data.system_name        ?? '').trim()
    const pv  = (parsed_data.vendor_name_raw    ?? '').trim()
    if (!psd && !psn && !pv) {
      rows_skipped_section_header++
      rows.push({
        row_no:            rowNo,
        raw_data,
        parsed_data,
        validation_errors: [],
        is_valid:          false,
        classification:    'invalid',
        skipped:           true,
        skip_reason:       'Section header row — no system or vendor data',
      })
      continue
    }

    // skip branch 3: explanation block (Q1-Q4 prose, criteria text, etc.)
    // same treatment as section_header, does NOT count toward the streak
    const explanation = isExplanationRow(parsed_data)
    if (explanation.isExplanation) {
      rows_skipped_explanation++
      rows.push({
        row_no:            rowNo,
        raw_data,
        parsed_data,
        validation_errors: [`Explanation/criteria row — not a system (${explanation.reason})`],
        is_valid:          false,
        classification:    'invalid',
        skipped:           true,
        skip_reason:       explanation.reason ?? 'Explanation row',
      })
      continue
    }

    // kept row: reset the streak, remember position for the
    // distance safety net
    consecutiveStructurallyEmpty = 0
    lastKeptRowIdx = r

    // fallback (engine-wide): a register whose identity column is headed
    // plain "System" maps it to system_description per the alias table,
    // leaving system_name empty. for a real (kept) row, surface that
    // identity as system_name so the system isn't left identified only by
    // its function group. fires ONLY when system_name is empty, registers
    // that populate both columns are untouched. placed after the section-
    // header / explanation skips so it can't perturb that detection
    if (!parsed_data.system_name?.trim() && parsed_data.system_description?.trim()) {
      parsed_data.system_name = parsed_data.system_description.trim()
    }

    const { contacts, isMulti, ambiguous } = splitContacts(
      parsed_data.contact_full_name,
      parsed_data.contact_email,
      parsed_data.contact_phone
    )

    if (isMulti && contacts.length > 0) {
      parsed_data.contacts      = contacts
      parsed_data.multi_contact = true
      parsed_data.contact_full_name = contacts[0].full_name
      parsed_data.contact_email     = contacts[0].email
      parsed_data.contact_phone     = contacts[0].phone
    } else if (contacts.length === 1) {
      parsed_data.contact_full_name = contacts[0].full_name
      parsed_data.contact_email     = contacts[0].email
      parsed_data.contact_phone     = contacts[0].phone
    }

    const validation_errors: string[] = []

    const hasSystem     = !!parsed_data.system_description
    const hasSystemName = !!parsed_data.system_name
    const hasVendor     = !!parsed_data.vendor_name_raw
    const hasContactName = !!(parsed_data.contact_full_name) ||
                           contacts.some(c => !!c.full_name)
    const hasEmail       = !!(parsed_data.contact_email) ||
                           contacts.some(c => !!c.email)

    if (!hasSystem && !hasSystemName) {
      validation_errors.push('Missing system description and name')
    } else if (!hasSystemName) {
      validation_errors.push('Missing system name (editable later)')
    }

    if (!hasVendor) {
      validation_errors.push('No vendor name')
    }

    if (hasContactName && !hasEmail) {
      validation_errors.push('Contact email missing (editable later)')
    } else if (!hasContactName) {
      validation_errors.push('No contact — system will be imported without contact link')
    }

    if (!parsed_data.type_approval_status) {
      validation_errors.push('Type approval status not set')
    }

    if (isMulti) {
      validation_errors.push(
        ambiguous
          ? `${contacts.length} contacts — name/email counts differ, review required`
          : `${contacts.length} contacts detected`
      )
    }

    const classification: RowClassification =
      validation_errors.length > 0 ? 'warning' : 'clean'

    rows.push({
      row_no: rowNo,
      raw_data,
      parsed_data,
      validation_errors,
      is_valid: true,
      classification,
    })
  }

  const skipped_count =
    rows_skipped_empty + rows_skipped_section_header + rows_skipped_explanation
  const last_data_row = lastKeptRowIdx > headerIdx ? lastKeptRowIdx + 1 : 0
  return {
    rows,
    skipped_count,
    last_data_row,
    rows_skipped_empty,
    rows_skipped_section_header,
    rows_skipped_explanation,
    stop_reason,
  }
}

// rebuild StagedRows from an already-parsed matrix using a different column mapping.
// used by the "adjust column mapping" UI after a user swaps which workbook column feeds each field.
// optional fillDownCols runs the same blank-cell inheritance pass used for the function column.
export function remapRows(
  matrix: unknown[][],
  headerIdx: number,
  colMap: Record<string, number>,
  fillDownCols: number[] = []
): StagedRow[] {
  for (const col of fillDownCols) {
    let lastValue: unknown = ''
    for (let r = headerIdx + 1; r < matrix.length; r++) {
      const cell = matrix[r]?.[col]
      const s = String(cell ?? '').trim()
      if (!s || /^[-–—\s]*$/.test(s)) {
        if (lastValue && matrix[r]) matrix[r][col] = lastValue
      } else {
        lastValue = cell
      }
    }
  }

  const internalColMap = new Map<number, ParsedField>()
  for (const [field, idx] of Object.entries(colMap)) {
    internalColMap.set(idx, field as ParsedField)
  }

  return buildStagedRows(matrix, headerIdx, internalColMap).rows
}

// ──────────────────────────────────────────────────────────────────────────
// Vendor Tracking format parser
//
// Sheet structure (verified against H5531 sample):
//   Row 1  — title cell (merged) "Vendor Data Tracking Sheet"
//   Row 2  — metadata (merged)
//   Row 3  — section group headers (merged Korean labels)
//   Row 4  — column headers (No. | CBS 등급 | 벤더사명 | CBS 시스템명 | …)
//   Row 5+ — data rows
//
// column mapping fixed by the workbook spec (no auto-detect needed).
// fields the spec marks "DROP" aren't stored, every column outside
// {A,B,C,D,E,F,M,Q} is ignored
// ──────────────────────────────────────────────────────────────────────────

const VT_HEADER_ROW = 4   // 1-indexed Excel row
const VT_DATA_START = 5
const VT_EMPTY_STREAK_STOP = 10

function vtCell(ws: XLSX.WorkSheet, row1: number, col1: number): unknown {
  const ref = XLSX.utils.encode_cell({ r: row1 - 1, c: col1 - 1 })
  const cell = ws[ref]
  return cell?.v ?? null
}

function vtNullIfBlank(v: unknown): string | null {
  if (v === null || v === undefined) return null
  const s = String(v).trim()
  return s === '' || s === '-' ? null : s
}

function parseVendorTracking(wb: XLSX.WorkBook): ParseResult | ParseError {
  const sheetName = wb.SheetNames.find(n => /^vendor[\s_]*tracking$/i.test(n.trim()))
  if (!sheetName) {
    return { error: 'Vendor Tracking sheet not found.' }
  }
  const ws = wb.Sheets[sheetName]

  // validate header row 4 anchors. if layout shifted, fail loud rather than
  // silently shifting all column indices
  const a4 = String(vtCell(ws, VT_HEADER_ROW, 1) ?? '').trim()
  const c4 = String(vtCell(ws, VT_HEADER_ROW, 3) ?? '').trim()
  const d4 = String(vtCell(ws, VT_HEADER_ROW, 4) ?? '').trim()
  if (a4 !== 'No.' || !c4.includes('벤더사') || !d4.includes('CBS')) {
    return {
      error:
        'Vendor Tracking sheet header row does not match expected layout. ' +
        'Expected row 4: No. | CBS 등급 | 벤더사명 | CBS 시스템명 | … ' +
        `(got A=${a4 || '∅'}, C=${c4 || '∅'}, D=${d4 || '∅'})`,
    }
  }

  // build the rawMatrix + headers + colMap views the existing UI expects, even
  // though Vendor Tracking's columns are fixed. lets the preview table render
  // without special-casing the format in ImportWorkbookPage's plumbing
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(ws, {
    header: 1, defval: '', raw: false,
  }) as unknown[][]
  const headerCells = matrix[VT_HEADER_ROW - 1] ?? []
  const headers = headerCells.map(c => String(c ?? '').trim())

  // Vendor Tracking's fixed mapping (col indices 0-based here)
  const colMap: Record<string, number> = {
    vendor_name_raw:      2,   // C
    system_name:          3,   // D
    contact_full_name:    4,   // E
    contact_email:        5,   // F (also holds phone, split below)
    type_approval_status: 12,  // M
    system_description:   16,  // Q
  }

  const range = ws['!ref'] ? XLSX.utils.decode_range(ws['!ref']) : null
  const lastRow1 = range ? range.e.r + 1 : VT_DATA_START + 200

  const rows: StagedRow[] = []
  let rows_skipped_empty = 0
  let rows_skipped_invalid = 0
  let consecutiveStructurallyEmpty = 0
  let lastKeptRowIdx = VT_HEADER_ROW - 1   // 0-indexed equivalent
  let stop_reason: 'end_of_sheet' | 'streak_break' | 'distance_break' = 'end_of_sheet'

  for (let r = VT_DATA_START; r <= lastRow1; r++) {
    // distance safety net, same shape as parseSystemRegister
    if ((r - 1) - lastKeptRowIdx > MAX_DISTANCE_FROM_LAST_KEPT) {
      stop_reason = 'distance_break'
      break
    }

    const cellA = vtCell(ws, r, 1)
    const cellB = vtCell(ws, r, 2)
    const cellC = vtCell(ws, r, 3)
    const cellD = vtCell(ws, r, 4)
    const cellE = vtCell(ws, r, 5)
    const cellF = vtCell(ws, r, 6)
    const cellM = vtCell(ws, r, 13)
    const cellQ = vtCell(ws, r, 17)

    // empty-row check across the cells we care about
    const allEmpty = [cellA, cellB, cellC, cellD, cellE, cellF, cellM, cellQ]
      .every(v => v === null || v === undefined || String(v).trim() === '')
    if (allEmpty) {
      rows_skipped_empty++
      consecutiveStructurallyEmpty++
      if (consecutiveStructurallyEmpty >= VT_EMPTY_STREAK_STOP) {
        stop_reason = 'streak_break'
        break
      }
      continue
    }
    consecutiveStructurallyEmpty = 0

    const vendor     = vtNullIfBlank(cellC)
    const systemName = vtNullIfBlank(cellD)

    // need at least vendor or system to be a real row. mid-sheet noise,
    // does NOT count toward the structurally-empty streak
    if (!vendor && !systemName) {
      rows_skipped_invalid++
      continue
    }

    // CBS 등급: strict A/B/C, otherwise null
    const gradeRaw = String(cellB ?? '').trim().toUpperCase()
    const cbsGrade = /^[ABC]$/.test(gradeRaw) ? gradeRaw : null

    // Cert(TA, SoC) col M: Y → 'O' (matches existing taBucket), N or empty → null
    const certRaw = String(cellM ?? '').trim().toUpperCase()
    const taStatus = certRaw === 'Y' ? 'O' : null

    const description = vtNullIfBlank(cellQ)

    const contacts = parseVendorTrackingContacts(cellE, cellF)

    const parsed_data: ParsedImportRow = {}
    if (vendor)      parsed_data.vendor_name_raw = vendor
    if (systemName)  parsed_data.system_name = systemName
    if (description) parsed_data.system_description = description
    if (taStatus)    parsed_data.type_approval_status = taStatus
    if (cbsGrade)    parsed_data.cbs_grade = cbsGrade

    // same engine-wide fallback as the system-register path: no dedicated
    // system_name but a description present → use the description as the
    // identity. only fires when system_name is empty
    if (!parsed_data.system_name?.trim() && parsed_data.system_description?.trim()) {
      parsed_data.system_name = parsed_data.system_description.trim()
    }

    if (contacts.length === 1) {
      const c = contacts[0]
      if (c.full_name) parsed_data.contact_full_name = c.full_name
      if (c.email)     parsed_data.contact_email = c.email
      if (c.phone)     parsed_data.contact_phone = c.phone
    } else if (contacts.length > 1) {
      parsed_data.contacts = contacts
      parsed_data.multi_contact = true
      const c = contacts[0]
      if (c.full_name) parsed_data.contact_full_name = c.full_name
      if (c.email)     parsed_data.contact_email = c.email
      if (c.phone)     parsed_data.contact_phone = c.phone
    }

    const validation_errors: string[] = []
    if (!vendor)       validation_errors.push('No vendor name')
    if (!systemName)   validation_errors.push('No system name')
    const hasEmail = contacts.some(c => !!c.email)
    const hasContactName = contacts.some(c => !!c.full_name)
    if (hasContactName && !hasEmail) {
      validation_errors.push('Contact email missing (editable later)')
    } else if (!hasContactName) {
      validation_errors.push('No contact — system will be imported without contact link')
    }
    if (!taStatus) validation_errors.push('Type approval status not set')
    if (contacts.length > 1) {
      validation_errors.push(`${contacts.length} contacts detected (same email/phone applied to all)`)
    }

    const classification: RowClassification =
      validation_errors.length > 0 ? 'warning' : 'clean'

    rows.push({
      row_no: r,
      raw_data: {
        'No.':            cellA,
        'CBS 등급':       cellB,
        '벤더사명':        cellC,
        'CBS 시스템명':    cellD,
        '담당자명':         cellE,
        '이메일/TEL':      cellF,
        'Cert':           cellM,
        'Functionality':  cellQ,
      },
      parsed_data,
      validation_errors,
      is_valid: true,
      classification,
    })
    lastKeptRowIdx = r - 1   // 0-indexed for the distance check
  }

  const telemetry: ParseTelemetry = {
    format: 'vendor_tracking',
    header_row: VT_HEADER_ROW,
    last_data_row: lastKeptRowIdx > VT_HEADER_ROW - 1 ? lastKeptRowIdx + 1 : 0,
    rows_kept: rows.length,
    rows_skipped_empty,
    rows_skipped_section_header: 0,
    rows_skipped_explanation: 0,
    rows_skipped_invalid,
    stop_reason,
  }

  return {
    format: 'vendor_tracking',
    rows,
    skipped_count: rows_skipped_empty + rows_skipped_invalid,
    sheet_used: sheetName,
    header_row: VT_HEADER_ROW,
    telemetry,
    headerIdx: VT_HEADER_ROW - 1,
    headers,
    rawMatrix: matrix,
    colMap,
  }
}

// Col E may contain multi-line names with optional team suffix:
//   "김민경 대리 / 기술팀\n황선휘 과장 / 영업팀"
// Col F holds email + phone, multi-line, with various label prefixes:
//   "Chloe.Jang@ksb.com\nTel: +82 51 899 8135"
//   "Email : kjbaek@dsrcorp.com\nTel : +82(0)51-979-0510"
//   "Tel : +82(0)70-…\nFax : +82(0)505-…\nEmail : sales@dlmkor.com │ "
// strategy:
//   1. split names on \n, drop "/ team" suffix on each line.
//   2. from F, drop Fax lines, find first email via regex, find first phone
//      via regex on lines without an email (or stripped of email).
//   3. apply (email, phone) to every parsed name. if no names, return one
//      contact with just email+phone (when at least one is non-null).
function parseVendorTrackingContacts(eCell: unknown, fCell: unknown): ImportContact[] {
  const nameLines = String(eCell ?? '')
    .split(/\r?\n/)
    .map(line => line.split('/')[0].trim())
    .filter(line => line.length > 0)

  const fLines = String(fCell ?? '')
    .split(/\r?\n/)
    .map(s => s.trim())
    .filter(s => s && !/^\s*fax\b/i.test(s))

  const EMAIL_RE = /[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}/i
  let email: string | undefined
  for (const line of fLines) {
    const m = line.match(EMAIL_RE)
    if (m) { email = m[0].toLowerCase(); break }
  }

  const PHONE_RE = /(\+?\d[\d\s().\-]{6,}\d)/
  let phone: string | undefined
  for (const line of fLines) {
    if (email && line.toLowerCase().includes(email)) {
      const stripped = line.replace(new RegExp(email, 'i'), '')
                            .replace(/email\s*[:：]?/i, '')
                            .trim()
      const m = stripped.match(PHONE_RE)
      if (m) { phone = m[1].trim(); break }
    } else {
      const m = line.match(PHONE_RE)
      if (m) { phone = m[1].trim(); break }
    }
  }

  if (nameLines.length === 0) {
    if (!email && !phone) return []
    const c: ImportContact = {}
    if (email) c.email = email
    if (phone) c.phone = phone
    return [c]
  }

  return nameLines.map(name => {
    const c: ImportContact = { full_name: name }
    if (email) c.email = email
    if (phone) c.phone = phone
    return c
  })
}

// ──────────────────────────────────────────────────────────────────────
// ROUTE DETECTION
//
// Two workbooks arrive at this door and mean opposite things.
//
//   Route A — the yard's project overview: a full system list with
//     contacts, uploaded up front. It CREATES systems.
//   Route B — our own vendor contact request, returned with the contact
//     columns filled in. Every system in it already exists as a
//     screening row; the yard added contacts and nothing else. It must
//     NOT create systems.
//
// Committing the wrong one silently is the failure to avoid: Route B
// read as Route A mints a duplicate register row for every system we
// already screened. So the route is DETECTED, shown, and overridable —
// never assumed.
// ──────────────────────────────────────────────────────────────────────

export const MARKER_SHEET_NAME = '_seacure'
export const SOURCE_COL_HEADER = '_src'

// the request form's exact header set, used only as a fallback when the
// markers are gone
const REQUEST_FORM_HEADERS = [
  'system name', 'model', 'vendor', 'drawing reference',
  'person in charge', 'e mail', 'tel', 'remark',
]

export type ImportRoute = 'overview' | 'contact_return'

export interface RouteDetection {
  route:      ImportRoute
  confidence: 'markers' | 'headers' | 'default'
  run_id:     string | null
  project_id: string | null
  // set when the file looks like ours but its traceability is gone —
  // surfaced in the preview so nobody reads a name match as certainty
  warning:    string | null
}

function readMarkers(wb: XLSX.WorkBook): Record<string, string> | null {
  const name = wb.SheetNames.find(n => n.trim().toLowerCase() === MARKER_SHEET_NAME)
  if (!name) return null
  const rows = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[name], { header: 1, defval: '', raw: false }) as unknown[][]
  const kv: Record<string, string> = {}
  for (const r of rows) {
    const k = String(r?.[0] ?? '').trim()
    if (k) kv[k] = String(r?.[1] ?? '').trim()
  }
  return kv
}

export function detectImportRoute(wb: XLSX.WorkBook): RouteDetection {
  const markers = readMarkers(wb)
  if (markers?.seacuredb === 'vendor-contact-request') {
    return {
      route: 'contact_return', confidence: 'markers',
      run_id: markers.run_id || null,
      project_id: markers.project_id || null,
      warning: markers.run_id ? null
        : 'This is our request form, but it carries no run id — rows will be matched by name and vendor.',
    }
  }

  // fallback: the request form's own header set, even if the markers
  // were stripped by a re-typed or re-saved file
  const selected = selectSheet(wb)
  if (selected) {
    const matrix = XLSX.utils.sheet_to_json<unknown[]>(selected.ws, { header: 1, defval: '', raw: false }) as unknown[][]
    for (let i = 0; i < Math.min(15, matrix.length); i++) {
      const heads = new Set((matrix[i] ?? []).map(c => normalizeHeader(String(c ?? ''))).filter(Boolean))
      if (REQUEST_FORM_HEADERS.every(h => heads.has(h))) {
        return {
          route: 'contact_return', confidence: 'headers',
          run_id: null, project_id: null,
          warning: 'This looks like our request form, but its identifying sheet is missing — '
                 + 'rows will be matched by name and vendor rather than by the ids we sent, '
                 + 'so check the matches before committing.',
        }
      }
    }
  }
  return { route: 'overview', confidence: 'default', run_id: null, project_id: null, warning: null }
}

export interface ContactReturnRow {
  row_no:      number
  source_ids:  string[]        // empty when the traceability column is gone
  system_name: string
  vendor:      string | null
  contacts:    ImportContact[]
  remark:      string | null
  // true when this row was not on the form we sent — the yard added it
  added_by_yard: boolean
}

export interface ContactReturnParse {
  detection:   RouteDetection
  sheet_used:  string
  header_row:  number
  rows:        ContactReturnRow[]
  blank_rows:  number           // the yard simply did not answer these
}

// Parse a returned request form. Deliberately narrow: it reads the
// contact columns and the traceability column, and nothing else. It
// never builds a system record, because Route B does not create systems.
export function parseContactReturn(wb: XLSX.WorkBook): ContactReturnParse | ParseError {
  const detection = detectImportRoute(wb)
  const selected = selectSheet(wb)
  if (!selected) return { error: 'No system sheet found in the returned form.' }

  const raw = XLSX.utils.sheet_to_json<unknown[]>(selected.ws, { header: 1, defval: '', raw: false }) as unknown[][]
  if (raw.length === 0) return { error: `Sheet "${selected.name}" is empty.` }
  const matrix = fillMergedCells(raw, selected.ws)

  let headerIdx = -1
  const col: Record<string, number> = {}
  for (let i = 0; i < Math.min(15, matrix.length); i++) {
    const map: Record<string, number> = {}
    for (let c = 0; c < (matrix[i] ?? []).length; c++) {
      const norm = normalizeHeader(String(matrix[i][c] ?? ''))
      if (!norm) continue
      const field = resolveHeaderField(
        norm, c > 0 ? normalizeHeader(String(matrix[i][c - 1] ?? '')) : null)
      if (field && map[field] === undefined) map[field] = c
      if (norm === normalizeHeader(SOURCE_COL_HEADER)) map.source_ids = c
    }
    if (map.contact_full_name !== undefined || map.contact_email !== undefined) {
      headerIdx = i; Object.assign(col, map); break
    }
  }
  if (headerIdx < 0) {
    return { error: 'Could not find the contact columns (Person in Charge / E-mail / TEL) in the returned form.' }
  }

  const rows: ContactReturnRow[] = []
  let blank_rows = 0
  for (let r = headerIdx + 1; r < matrix.length; r++) {
    const cell = (f: string) => f in col ? String(matrix[r]?.[col[f]] ?? '').trim() : ''
    const system_name = cell('system_name')
    const nameCol = system_name || (col.system_description !== undefined ? cell('system_description') : '')
    if (!nameCol) continue                      // spacer / legend row

    const { contacts } = splitContacts(cell('contact_full_name'), cell('contact_email'), cell('contact_phone'))
    if (contacts.length === 0) { blank_rows++; continue }   // simply unanswered

    const src = cell('source_ids').split(/[\s,;]+/).filter(x => /^[0-9a-f-]{36}$/i.test(x))
    rows.push({
      row_no: r + 1,
      source_ids: src,
      system_name: nameCol,
      vendor: cell('vendor_name_raw') || null,
      contacts,
      remark: cell('remark') || null,
      // no id AND the file was supposed to carry ids => the yard added it
      added_by_yard: src.length === 0 && detection.confidence === 'markers' && col.source_ids !== undefined,
    })
  }

  return { detection, sheet_used: selected.name, header_row: headerIdx + 1, rows, blank_rows }
}
